// Admin · Feedback. This file and server/admin_feedback.go are the whole section.
//
// WHAT THIS IS FOR. Operator reports have been arriving since the console
// shipped and landing where only the operator's own client could read them
// (listFeedback scopes to the caller's client_id). This is the reader: every
// client's reports in one list, newest first, with the state the console was in
// when each was written.
//
// THE CONTEXT IS THE POINT. "The wave chart looks wrong" is nearly useless a
// week later; the same sentence carrying the point, the vessel, the operation
// and the hour under the playhead is reproducible. The server stores that blob
// verbatim and never validates it, so this file must read it the same way it was
// written — take the keys it recognises, print the ones it does not, and never
// throw on a shape it has not seen. A report that arrives with a new key is a
// report we still have to be able to read.
//
// NEVER innerHTML. Everything below is user-submitted text rendered into an
// authenticated page; it is built with createElement and textContent, without
// exception.
//
// No new CSS: this uses the shared admin vocabulary from style.css (.ad-card,
// .ad-table, .ad-toolbar, .ad-when, .ad-empty, .ad-state, .ad-kv).

/** One report, as this page holds it. `context` stays `unknown` on purpose. */
interface FeedbackItem {
  id: number;
  createdAt: string;
  clientId: number;
  client: string;
  /** null when the account that wrote it has been removed — not the same as "". */
  author: string | null;
  message: string;
  context: unknown;
  /** null while unread. */
  seenAt: string | null;
}

/** One entry in the client filter. Comes from the data, never from a list here. */
interface ClientRow {
  id: number;
  name: string;
  total: number;
  unseen: number;
}

interface FeedbackPage {
  items: FeedbackItem[];
  clients: ClientRow[];
  limit: number;
  truncated: boolean;
}

// --- narrowing ---------------------------------------------------------------

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function narrowItem(raw: unknown): FeedbackItem | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const id = num(r.id);
  const createdAt = str(r.created_at);
  const message = str(r.message);
  if (id === null || createdAt === null || message === null) return null;
  return {
    id,
    createdAt,
    clientId: num(r.client_id) ?? 0,
    client: str(r.client) ?? "",
    author: str(r.author),
    message,
    context: r.context,
    seenAt: str(r.seen_at),
  };
}

function narrowPage(raw: unknown): FeedbackPage | null {
  const r = asRecord(raw);
  if (r === null || !Array.isArray(r.feedback)) return null;
  const items: FeedbackItem[] = [];
  for (const entry of r.feedback) {
    const it = narrowItem(entry);
    if (it !== null) items.push(it);
  }
  const clients: ClientRow[] = [];
  if (Array.isArray(r.clients)) {
    for (const entry of r.clients) {
      const c = asRecord(entry);
      if (c === null) continue;
      const id = num(c.id);
      const name = str(c.name);
      if (id === null || name === null) continue;
      clients.push({ id, name, total: num(c.total) ?? 0, unseen: num(c.unseen) ?? 0 });
    }
  }
  return {
    items,
    clients,
    limit: num(r.limit) ?? items.length,
    truncated: r.truncated === true,
  };
}

// --- time --------------------------------------------------------------------

/** "2026-08-13 14:02 UTC" — absolute, unambiguous, and the same on every desk. */
function utcStamp(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toISOString().slice(0, 16).replace("T", " ") + " UTC";
}

/** "3 days ago". Coarse on purpose — this answers "is this fresh", not "when". */
function relativeStamp(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const secs = Math.round((now - t) / 1000);
  // A clock a little ahead of the server reads as "just now", not "in -3 s".
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return days === 1 ? "1 day ago" : `${days} days ago`;
  const months = Math.round(days / 30.4);
  if (months < 24) return months === 1 ? "1 month ago" : `${months} months ago`;
  return `${Math.round(months / 12)} years ago`;
}

// --- context -----------------------------------------------------------------

/** One label/value pair of unpacked context. */
type ContextRow = [string, string];

function safeStringify(v: unknown): string {
  try {
    const s = JSON.stringify(v);
    if (s === undefined) return String(v);
    return s.length > 240 ? s.slice(0, 240) + "…" : s;
  } catch {
    return String(v);
  }
}

/** A position reads as a position: "27.500 N, 94.000 W", never as raw JSON. */
function fmtPosition(lat: number, lon: number): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(3)} ${ns}, ${Math.abs(lon).toFixed(3)} ${ew}`;
}

/**
 * Keys this page knows how to print, in reading order. Everything not listed
 * still gets printed, raw, under its own key — dropping an unrecognised field
 * would quietly lose the one detail that explains a report.
 */
const KNOWN_KEYS: readonly string[] = [
  "lat",
  "lon",
  "vessel",
  "operation",
  "cursorUtc",
  "windowHours",
  "windLimitKn",
  "waveLimitM",
  "limitsProvisional",
  "route",
  "viewport",
  "appVersion",
];

function contextRows(raw: unknown): ContextRow[] {
  const rows: ContextRow[] = [];
  const c = asRecord(raw);
  if (c === null) {
    // Not an object at all (an old report, or something unexpected). Show it
    // rather than pretending there was no context.
    if (typeof raw === "string" && raw !== "") rows.push(["context", raw]);
    else if (raw !== null && raw !== undefined) rows.push(["context", safeStringify(raw)]);
    return rows;
  }

  const lat = num(c.lat);
  const lon = num(c.lon);
  if (lat !== null && lon !== null) rows.push(["position", fmtPosition(lat, lon)]);

  const vessel = str(c.vessel);
  if (vessel !== null && vessel !== "") rows.push(["vessel", vessel]);

  const operation = str(c.operation);
  if (operation !== null && operation !== "") rows.push(["operation", operation]);

  const cursor = str(c.cursorUtc);
  if (cursor !== null && cursor !== "") rows.push(["hour in view", utcStamp(cursor)]);

  const windowHours = num(c.windowHours);
  if (windowHours !== null) rows.push(["window", `${windowHours} h`]);

  // The limits travel together or not at all; a wind limit with no wave limit
  // beside it has been true of older reports, so each is printed if present.
  const limitBits: string[] = [];
  const windLimit = num(c.windLimitKn);
  if (windLimit !== null) limitBits.push(`${windLimit} kn wind`);
  const waveLimit = num(c.waveLimitM);
  if (waveLimit !== null) limitBits.push(`${waveLimit} m wave`);
  if (limitBits.length > 0) {
    rows.push([
      "limits",
      limitBits.join(" · ") + (c.limitsProvisional === true ? " (provisional)" : ""),
    ]);
  }

  const route = str(c.route);
  if (route !== null && route !== "") rows.push(["route", route]);

  const viewport = str(c.viewport);
  if (viewport !== null && viewport !== "") rows.push(["viewport", viewport.replace("x", " × ")]);

  const build = str(c.appVersion);
  if (build !== null && build !== "") rows.push(["build", build]);

  // Everything else, as it arrived. The shape changes as the console does.
  for (const key of Object.keys(c)) {
    if (KNOWN_KEYS.includes(key)) continue;
    const v = c[key];
    if (v === null || v === undefined || v === "") continue;
    rows.push([key, typeof v === "string" ? v : safeStringify(v)]);
  }
  return rows;
}

// --- DOM ---------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

function contextList(raw: unknown): HTMLElement | null {
  const rows = contextRows(raw);
  if (rows.length === 0) return null;
  const dl = el("dl", "ad-kv");
  for (const [label, value] of rows) {
    dl.append(el("dt", undefined, label), el("dd", "mono", value));
  }
  return dl;
}

export function mountAdminFeedback(host: HTMLElement): void {
  const card = el("section", "ad-card");
  card.appendChild(el("h2", undefined, "Operator feedback — every client"));

  const top = el("div", "ad-body");
  const bar = el("div", "ad-toolbar");
  const label = el("label", "ad-when", "Client");
  const picker = el("select", "sel");
  picker.id = "ad-feedback-client";
  label.htmlFor = picker.id;
  const counts = el("span", "ad-when");
  const status = el("span", "ad-when ad-spacer");
  bar.append(label, picker, counts, status);
  top.appendChild(bar);

  const region = el("div");
  card.append(top, region);
  host.appendChild(card);

  let page: FeedbackPage | null = null;
  /** "" = all clients; otherwise the client id as a string. */
  let filter = "";

  const showState = (text: string, retry?: () => void): void => {
    const box = el("p", "ad-empty", text);
    region.replaceChildren(box);
    if (retry !== undefined) {
      const again = el("button", "btn", "Try again");
      again.type = "button";
      again.addEventListener("click", retry);
      const wrap = el("div", "ad-body");
      wrap.appendChild(again);
      region.appendChild(wrap);
    }
  };

  const paintPicker = (): void => {
    if (page === null) return;
    picker.replaceChildren();
    const all = el("option", undefined, "All clients");
    all.value = "";
    picker.appendChild(all);
    for (const c of page.clients) {
      const o = el("option", undefined, c.unseen > 0 ? `${c.name} — ${c.unseen} unseen` : c.name);
      o.value = String(c.id);
      picker.appendChild(o);
    }
    picker.value = filter;
    // A client that vanished from the data (its last report deleted) must not
    // leave the filter pointing at nothing.
    if (picker.value !== filter) {
      filter = "";
      picker.value = "";
    }
  };

  const paintCounts = (): void => {
    if (page === null) return;
    let total = 0;
    let unseen = 0;
    for (const c of page.clients) {
      if (filter !== "" && String(c.id) !== filter) continue;
      total += c.total;
      unseen += c.unseen;
    }
    const reports = total === 1 ? "1 report" : `${total} reports`;
    counts.textContent = unseen > 0 ? `${reports} · ${unseen} unseen` : `${reports} · all read`;
  };

  const markSeen = (item: FeedbackItem, seen: boolean, button: HTMLButtonElement): void => {
    button.disabled = true;
    status.textContent = "";
    void (async () => {
      let res: Response;
      try {
        res = await fetch(`/api/admin/feedback/${item.id}`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ seen }),
        });
      } catch {
        button.disabled = false;
        status.textContent = "Network error — the report was not updated.";
        return;
      }
      if (!res.ok) {
        button.disabled = false;
        status.textContent = "Could not update that report.";
        return;
      }
      let seenAt: string | null = null;
      try {
        seenAt = str(asRecord(await res.json())?.seen_at);
      } catch {
        // The write landed; only the echo was unreadable. Fall back to now,
        // which is within a second of what the server just stored.
        seenAt = seen ? new Date().toISOString() : null;
      }
      item.seenAt = seen ? (seenAt ?? new Date().toISOString()) : null;
      if (page !== null) {
        for (const c of page.clients) {
          if (c.id !== item.clientId) continue;
          c.unseen = Math.max(0, c.unseen + (seen ? -1 : 1));
        }
      }
      paintPicker();
      paintCounts();
      paintRows();
    })();
  };

  function rowFor(item: FeedbackItem, now: number): HTMLTableRowElement {
    const seen = item.seenAt !== null;
    // Seen rows are dimmed rather than unseen rows being lit: .muted is the one
    // row treatment the shared stylesheet has, and what is new should be what
    // reads at full strength.
    const tr = el("tr", seen ? "muted" : undefined);

    const when = el("td", "mono");
    when.append(
      el("div", undefined, relativeStamp(item.createdAt, now)),
      el("div", "ad-when", utcStamp(item.createdAt)),
    );

    const who = el("td");
    if (item.author === null || item.author === "") {
      who.appendChild(el("span", "ad-when", "account removed"));
    } else {
      who.textContent = item.author;
    }

    const report = el("td");
    report.appendChild(el("p", "ad-note-body", item.message));
    const ctx = contextList(item.context);
    if (ctx !== null) report.appendChild(ctx);

    const mark = el("td");
    const state = el("span", seen ? "ad-state" : "ad-state warn", seen ? "seen" : "new");
    if (seen && item.seenAt !== null) state.title = `Marked seen ${utcStamp(item.seenAt)}`;
    const button = el("button", "btn", seen ? "Mark unseen" : "Mark seen");
    button.type = "button";
    button.addEventListener("click", () => markSeen(item, !seen, button));
    mark.append(state, el("div"), button);

    tr.append(when, el("td", undefined, item.client), who, report, mark);
    return tr;
  }

  function paintRows(): void {
    if (page === null) return;
    const shown =
      filter === "" ? page.items : page.items.filter((i) => String(i.clientId) === filter);

    if (page.items.length === 0) {
      showState("No feedback yet. Nothing has been reported from any client.");
      return;
    }
    if (shown.length === 0) {
      showState(`Nothing from this client in the newest ${page.limit} reports.`);
      return;
    }

    const table = el("table", "ad-table");
    const thead = el("thead");
    const hr = el("tr");
    for (const h of ["When", "Client", "Person", "Report", "Read"]) {
      hr.appendChild(el("th", undefined, h));
    }
    thead.appendChild(hr);
    const tbody = el("tbody");
    const now = Date.now();
    for (const item of shown) tbody.appendChild(rowFor(item, now));
    table.append(thead, tbody);
    region.replaceChildren(table);

    if (page.truncated) {
      region.appendChild(
        el(
          "p",
          "ad-empty",
          `Showing the newest ${page.limit} reports — older ones are not listed.`,
        ),
      );
    }
  }

  const load = (): void => {
    showState("Loading feedback…");
    void (async () => {
      let res: Response;
      try {
        res = await fetch("/api/admin/feedback", { credentials: "same-origin" });
      } catch {
        showState("Could not load feedback — the server did not answer.", load);
        return;
      }
      if (!res.ok) {
        showState("Could not load feedback.", load);
        return;
      }
      let parsed: FeedbackPage | null = null;
      try {
        parsed = narrowPage(await res.json());
      } catch {
        parsed = null;
      }
      if (parsed === null) {
        showState("Could not load feedback — the response was not readable.", load);
        return;
      }
      page = parsed;
      paintPicker();
      paintCounts();
      paintRows();
    })();
  };

  picker.addEventListener("change", () => {
    filter = picker.value;
    status.textContent = "";
    paintCounts();
    paintRows();
  });

  load();
}
